import { AnimatePresence, motion } from "motion/react";
import { FC, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import {
  DepositProvider,
  networkTxLink,
  PendingDeposit,
  type Network,
} from "rujira.js";

import { nFormatter } from "../../helpers";
import { Animated } from "../icons/Animated";
import { Xmark } from "../icons/Icons";
import { NetworkIcon } from "../icons/NetworkIcon";

export interface PendingDepositProps {
  /** deprecated - use depositProvider to prevent unncessary re-renders */
  deposits: PendingDeposit[];
  dismissDeposit: (hash: string) => void;
  depositProvider?: DepositProvider;
  clearAll?: () => void;
  operation?: {
    label: string;
    phase: "preparing" | "submitting" | "pending" | "confirmed" | "failed";
    startedAt: number;
    network: Network;
    txHash?: string;
    message?: string;
    checking?: boolean;
    finalTransaction?: boolean;
  };
  dismissOperation?: () => void;
  checkOperation?: () => Promise<void>;
}

export const PendingDeposits = ({
  deposits,
  dismissDeposit,
  clearAll,
  operation,
  dismissOperation,
  checkOperation,
}: PendingDepositProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const pendingRoot = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showMenu) return;
    const closeOutside = (event: PointerEvent) => {
      if (
        event.target instanceof Node &&
        !pendingRoot.current?.contains(event.target)
      )
        setShowMenu(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [showMenu]);
  if (!deposits.length && !operation) return null;
  const isAlert =
    !!operation || !!deposits.find((x) => x.status === "succeeded");
  const operationPending =
    operation?.phase === "preparing" ||
    operation?.phase === "submitting" ||
    operation?.phase === "pending";
  const operationConfirmed =
    operation?.phase === "confirmed" && operation.finalTransaction !== false;

  return (
    <div
      ref={pendingRoot}
      className={clsx({
        "rujira-header__pending": true,
        "rujira-header__alert": isAlert,
      })}
      role="button"
      tabIndex={0}
      aria-label={
        operation
          ? `${operation.label}: ${operationPending ? "pending" : operationConfirmed ? "confirmed" : "needs attention"}`
          : "Pending transactions"
      }
      aria-expanded={showMenu}
      onClick={() => setShowMenu(true)}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setShowMenu((open) => !open);
        }
        if (event.key === "Escape") setShowMenu(false);
      }}
      onMouseOver={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}>
      <Animated
        name="alert"
        paused={!isAlert}
        aria-label="Pending transactions"
      />
      {operation && (
        <span role="status" className="visually-hidden">
          {operation.label}:{" "}
          {operationPending
            ? "pending"
            : operationConfirmed
              ? "confirmed"
              : "needs attention"}
        </span>
      )}
      <AnimatePresence>
        {showMenu && (
          <motion.div
            className="rujira-header__popup sub-nav fw-400 px-2 py-1.5"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}>
            {operation && (
              <PrivateOperation
                operation={operation}
                dismiss={dismissOperation}
                check={checkOperation}
              />
            )}
            {deposits.length > 0 && (
              <div className="text-right mb-1.5">
                <a className="tag tag--md hover-white" onClick={clearAll}>
                  clear all
                </a>
              </div>
            )}
            {[...deposits].sort(sorter).map((x) => (
              <Deposit
                key={x.hash}
                deposit={x}
                dismiss={() => dismissDeposit(x.hash)}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PrivateOperation: FC<{
  operation: NonNullable<PendingDepositProps["operation"]>;
  dismiss?: () => void;
  check?: () => Promise<void>;
}> = ({ operation, dismiss, check }) => {
  const pending =
    operation.phase === "preparing" ||
    operation.phase === "submitting" ||
    operation.phase === "pending";
  const confirmed =
    operation.phase === "confirmed" && operation.finalTransaction !== false;
  const status = pending ? "pending" : confirmed ? "confirmed" : "check status";
  return (
    <div
      className="flex rujira-header__pending-tx mb-2"
      style={{ whiteSpace: "normal", minWidth: "min(18rem, 70vw)" }}>
      <NetworkIcon network={operation.network} className="w-3 h-3 mr-1" />
      <div className="flex dir-c grow">
        <div className="flex ai-c">
          <span className="fs-14 condensed color-white mr-2">
            {operation.label}
          </span>
          <span
            className={clsx(
              "tag tag--sm ar",
              pending ? "tag--orange" : confirmed ? "tag--teal" : "tag--red"
            )}>
            <b /> {status}
          </span>
        </div>
        <span className="fs-13 condensed color-grey mt-0.5">
          {operation.phase === "preparing"
            ? "Preparing your transaction…"
            : operation.phase === "submitting"
              ? "Submitting your transaction…"
              : operation.message ||
                (confirmed
                  ? "Transaction confirmed."
                  : operation.phase === "confirmed"
                    ? "Review your transaction to continue."
                    : "Check the result before trying again.")}
        </span>
        <div className="flex mt-0.5">
          <span className="fs-13 condensed color-grey mr-2">
            {new Date(operation.startedAt).toLocaleTimeString()}
          </span>
          {operation.txHash && (
            <a
              href={networkTxLink({
                network: operation.network,
                txHash: operation.txHash,
              })}
              target="_blank"
              rel="noopener noreferrer"
              className="fs-13 condensed color-grey no-underline hover-white ar">
              {operation.txHash.slice(0, 4)}...{operation.txHash.slice(-6)}
            </a>
          )}
        </div>
        {operation.phase === "pending" && check && (
          <button
            type="button"
            className="transparent color-white pointer fs-13 mt-1"
            disabled={operation.checking}
            onClick={(event) => {
              event.stopPropagation();
              void check();
            }}>
            {operation.checking ? "Checking status…" : "Check status"}
          </button>
        )}
      </div>
      {!pending && dismiss && (
        <button
          type="button"
          aria-label="Dismiss private transaction"
          className="transparent ml-1.5 mr-0 color-grey hover-white pointer"
          onClick={(event) => {
            event.stopPropagation();
            dismiss();
          }}>
          <Xmark className="w-2 h-2" />
        </button>
      )}
    </div>
  );
};

const Deposit: FC<{ deposit: PendingDeposit; dismiss: () => void }> = ({
  deposit,
  dismiss,
}) => {
  const { hash, status, network, timestamp, message } = deposit;

  const [elapsed, setElapsed] = useState(
    (new Date().getTime() - timestamp.getTime()) / 1000
  );
  useEffect(() => {
    const int = setInterval(() => {
      setElapsed((new Date().getTime() - timestamp.getTime()) / 1000);
    }, 1000);
    return () => clearInterval(int);
  }, []);

  return (
    <div className="flex rujira-header__pending-tx mb-2">
      <NetworkIcon network={network} className="w-3 h-3 mr-1" />
      <div className="flex dir-c grow">
        <div className="flex">
          <div className="fs-14 condensed color-white">
            {nFormatter(deposit.coin.amount, 6, 8)} {deposit.coin.symbol}
          </div>

          <div className="ar flex">
            {status === "pending" && (
              <div className="fs-12 condensed color-grey mr-2 wrap-reset">
                <span className="color-white">
                  {elapsed < 60
                    ? `${elapsed}s`
                    : elapsed < 3600
                      ? `${Math.floor(elapsed / 60)}m ${Math.floor(elapsed % 60)}s`
                      : elapsed < 36000
                        ? `${Math.floor(elapsed / 3600)}h ${Math.floor((elapsed % 3600) / 60)}m ${Math.floor(elapsed % 60)}s`
                        : "> 10h"}
                </span>
              </div>
            )}

            <div
              className={clsx({
                "tag tag--sm": true,
                "tag--orange": status === "pending",
                "tag--teal": status === "succeeded",
                "tag--red": ["refunded", "failed"].includes(status),
              })}>
              <b /> {status}
            </div>
          </div>
        </div>
        <div className="flex mt-0.5">
          <span className="fs-13 condensed color-grey no-underline mr-3">
            {timestamp.toLocaleTimeString()} {timestamp.toLocaleDateString()}
          </span>
          <a
            href={networkTxLink({ network, txHash: hash })}
            target="_blank"
            className="fs-13 condensed color-grey no-underline hover-white ar">
            {hash.slice(0, 4)}...{hash.slice(-6)}
          </a>
        </div>
        {message && (
          <div className="flex mt-0.5">
            <span className="fs-13 condensed color-orange no-underline mr-3">
              {message}
            </span>
          </div>
        )}
      </div>
      {status !== "pending" && (
        <button
          className="transparent ml-1.5 mr-0 color-grey hover-white pointer"
          onClick={dismiss}>
          <Xmark className="w-2 h-2" />
        </button>
      )}
    </div>
  );
};

const sorter = (a: PendingDeposit, b: PendingDeposit): number =>
  b.timestamp.getTime() - a.timestamp.getTime();
